import { useRef, useState } from "react";
import { Button, ErrorText, Panel, Spinner } from "../../components/ui";
import { fmtDate } from "../../lib/format";
import { useBatch, useImportBatches, useImportCsv } from "./hooks";

const SAMPLE = "ticker,price_date,close,previous_close\nSNTS,2026-05-02,24800,24000\nBOAB,2026-05-02,4550,4500";

export function ImportPage() {
  const importCsv = useImportCsv();
  const batches = useImportBatches();
  const [batchId, setBatchId] = useState<string | null>(null);
  const batch = useBatch(batchId);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const csv = await file.text();
    importCsv.mutate(
      { csv, filename: file.name },
      { onSuccess: (r) => setBatchId(r.batchId) },
    );
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <Panel title="Import CSV des cours">
      <p className="muted">
        En-tête : <code>ticker,price_date,close[,previous_close,open,high,low,volume]</code> ·
        date <code>AAAA-MM-JJ</code>. Exemple :
      </p>
      <pre className="sample">{SAMPLE}</pre>

      <div className="inline-form">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} />
        {importCsv.isPending && <Spinner />}
      </div>
      <ErrorText error={importCsv.error} />

      {batch.data && (
        <div className="batch-result">
          <h3>
            Import {batch.data.status === "processing" ? "en cours…" : batch.data.status}{" "}
            {batch.data.status === "done" && (
              <>
                — {batch.data.successCount}/{batch.data.rowCount} lignes,{" "}
                {batch.data.errorCount} erreur(s)
              </>
            )}
          </h3>
          {batch.data.errorLog?.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ligne</th>
                    <th>Ticker</th>
                    <th>Erreur</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.data.errorLog.map((err, i) => (
                    <tr key={i}>
                      <td>{err.line}</td>
                      <td>{err.ticker ?? "—"}</td>
                      <td>{err.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <h3>Imports récents</h3>
      {batches.isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fichier</th>
                <th>Statut</th>
                <th>OK / total</th>
                <th>Erreurs</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {batches.data?.map((b) => (
                <tr
                  key={b.id}
                  className={b.id === batchId ? "row-active" : undefined}
                  onClick={() => setBatchId(b.id)}
                >
                  <td>{b.filename ?? "—"}</td>
                  <td>
                    <span className={`tag tag-${b.status}`}>{b.status}</span>
                  </td>
                  <td>
                    {b.successCount ?? 0} / {b.rowCount ?? 0}
                  </td>
                  <td>{b.errorCount ?? 0}</td>
                  <td>{fmtDate(b.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
